/**
 * Stack frame parser
 *
 * Parses an `Error().stack` string and returns the first non-logger frame.
 * Handles V8 (Chrome/Edge), Firefox, and Safari JSC formats. Never throws —
 * returns `undefined` when no frame can be parsed (e.g. Safari prod stacks
 * occasionally lack line numbers).
 */

export interface ParsedFrame {
  file: string;
  line: number;
  column: number;
  functionName: string | undefined;
}

const V8_WITH_PARENS = /^\s*at\s+(?:(.+?)\s+)?\((.+?):(\d+):(\d+)\)\s*$/;
const V8_BARE = /^\s*at\s+(.+?):(\d+):(\d+)\s*$/;
const FIREFOX_SAFARI = /^(.*?)@(.+?):(\d+):(\d+)\s*$/;

function normalizeFile(rawFile: string): string {
  let file = rawFile.replace(/\?[^:]*/, '');
  // Strip scheme + host before resolving the /src/ shortcut so a CDN path
  // with /src/ as a route segment (e.g. https://cdn.example.com/src/...)
  // can't match against the host portion.
  file = file.replace(/^[a-z]+:\/+/, '');
  const slashIdx = file.indexOf('/');
  if (slashIdx > 0 && file.slice(0, slashIdx).includes('.')) {
    file = file.slice(slashIdx + 1);
  }
  const srcIdx = file.lastIndexOf('/src/');
  if (srcIdx !== -1) {
    return file.slice(srcIdx + '/src/'.length);
  }
  if (file.startsWith('src/')) {
    return file.slice('src/'.length);
  }
  return file;
}

function parseLine(line: string): ParsedFrame | undefined {
  let m = V8_WITH_PARENS.exec(line);
  if (m) {
    const fn = m[1]?.trim();
    return {
      functionName: fn ? fn : undefined,
      file: normalizeFile(m[2]),
      line: Number(m[3]),
      column: Number(m[4]),
    };
  }
  m = V8_BARE.exec(line);
  if (m) {
    return {
      functionName: undefined,
      file: normalizeFile(m[1]),
      line: Number(m[2]),
      column: Number(m[3]),
    };
  }
  m = FIREFOX_SAFARI.exec(line);
  if (m) {
    const fn = m[1].trim();
    return {
      functionName: fn ? fn : undefined,
      file: normalizeFile(m[2]),
      line: Number(m[3]),
      column: Number(m[4]),
    };
  }
  return undefined;
}

export function parseFirstCallerFrame(
  stack: string | undefined,
  skipFiles: string[],
): ParsedFrame | undefined {
  try {
    if (!stack) return undefined;
    const lines = stack.split('\n');
    for (const raw of lines) {
      const frame = parseLine(raw);
      if (!frame) continue;
      // Match on the basename's stem (everything before the last extension)
      // so `'analyticsService'` doesn't also skip `'analyticsService.test.ts'`.
      const basename = frame.file.substring(frame.file.lastIndexOf('/') + 1);
      const lastDot = basename.lastIndexOf('.');
      const stem = lastDot > 0 ? basename.substring(0, lastDot) : basename;
      if (skipFiles.some((skip) => stem === skip)) continue;
      return frame;
    }
    return undefined;
  } catch {
    return undefined;
  }
}
